import { Podcast, Episode } from '../types';

async function fetchWithCorsFallback(url: string): Promise<string> {
  try {
    // Try direct fetch first
    const response = await fetch(url);
    if (!response.ok) throw new Error('Network response was not ok.');
    return await response.text();
  } catch (error) {
    // If direct fetch fails, try with CORS proxy
    console.log('Direct fetch failed, trying CORS proxy...', error);
    try {
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`;
      const response = await fetch(proxyUrl);
      if (!response.ok) throw new Error('Proxy response was not ok.');
      const data = await response.json();
      return data.contents;
    } catch (proxyError) {
      console.error('Both direct and proxy fetch failed:', proxyError);
      throw error; // Throw original error
    }
  }
}

export async function fetchAndParseFeed(feedUrl: string): Promise<{ podcast: Podcast; episodes: Episode[] }> {
  try {
    const text = await fetchWithCorsFallback(feedUrl);
    const parser = new DOMParser();
    const xml = parser.parseFromString(text, 'text/xml');

    const channel = xml.querySelector('channel');
    if (!channel) {
      throw new Error('Invalid RSS feed');
    }

    const podcastId = generateId(feedUrl);
    const podcast: Podcast = {
      id: podcastId,
      title: channel.querySelector('title')?.textContent || 'Unknown Podcast',
      description: channel.querySelector('description')?.textContent || '',
      author: channel.querySelector('author, itunes\\:author')?.textContent || '',
      imageUrl: channel.querySelector('image url, itunes\\:image')?.getAttribute('href') ||
                channel.querySelector('image url')?.textContent || '',
      feedUrl,
      addedAt: Date.now(),
    };

    const items = Array.from(xml.querySelectorAll('item'));
    console.log(`Found ${items.length} episodes in feed for ${podcast.title}`);

    const episodes: Episode[] = items.map((item, index) => {
      const enclosure = item.querySelector('enclosure');
      const audioUrl = enclosure?.getAttribute('url') || '';
      const pubDateStr = item.querySelector('pubDate')?.textContent || '';
      const durationStr = item.querySelector('itunes\\:duration')?.textContent || '0';

      return {
        id: generateId(audioUrl || `${podcastId}-${index}`),
        podcastId,
        title: item.querySelector('title')?.textContent || 'Untitled Episode',
        description: item.querySelector('description')?.textContent || '',
        audioUrl,
        pubDate: pubDateStr,
        duration: parseDuration(durationStr),
        episodeNumber: extractEpisodeNumber(item.querySelector('title')?.textContent || ''),
        downloaded: false,
      };
    });

    return { podcast, episodes };
  } catch (error) {
    console.error('Error parsing feed:', error);
    throw new Error('Failed to parse podcast feed');
  }
}

function generateId(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
}

function parseDuration(duration: string): number {
  if (!duration) return 0;

  const parts = duration.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  } else if (parts.length === 1) {
    return parts[0];
  }
  return 0;
}

function extractEpisodeNumber(title: string): number | undefined {
  const match = title.match(/\b(\d+):/);
  return match ? parseInt(match[1], 10) : undefined;
}

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}
